import * as Sentry from '@sentry/react';
import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { adminApi } from '@/lib/api';
import { User, UserRole, Gym } from '@/types';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import {
  Plus,
  Search,
  Pencil,
  Shield,
  Check,
  X,
  Loader2,
  Trash2,
  Key
} from 'lucide-react';

export default function UsersPage() {
  const { user: currentUser, setSessionLock, hasRole } = useAuth();
  const isSuperAdmin = hasRole(['super_admin']);

  const [users, setUsers] = useState<User[]>([]);
  const [gyms, setGyms] = useState<Gym[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);
  const [resettingUserId, setResettingUserId] = useState<string | null>(null);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'gym_staff' as UserRole,
    gym_id: currentUser?.gym_id || '',
    active: true,
  });

  useEffect(() => {
    fetchData();
  }, [currentUser?.gym_id]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const [usersRes, gymsRes] = await Promise.all([
        adminApi.listUsers(),
        isSuperAdmin ? adminApi.listGyms() : Promise.resolve({ success: true, gyms: [] })
      ]);

      if (usersRes.success) setUsers(usersRes.users);
      if (gymsRes.success) setGyms(gymsRes.gyms);
    } catch (error) {
      Sentry.captureException(error, { tags: { operation: 'users_fetch_data' } });
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los datos',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const filteredUsers = users.filter(user =>
    (user.name?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
    user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.user_id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getGymName = (gymId: string | null) => {
    if (!gymId) return 'Sistema (Global)';
    const gym = gyms.find(g => g.gym_id === gymId);
    return gym?.name || gymId;
  };

  const handleCreateUser = async () => {
    setIsSubmitting(true);
    setSessionLock(true); // Lock session sync during creation
    try {
      console.log('[UsersPage] Submitting formData:', formData);
      const res = await adminApi.createUser(formData);
      if (res.success) {
        toast({
          title: 'Usuario creado',
          description: `${formData.email} ha sido registrado exitosamente`,
        });
        setIsCreateOpen(false);
        resetForm();
        fetchData();
      } else {
        toast({
          title: 'Error',
          description: res.error || 'No se pudo crear el usuario',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      Sentry.captureException(error, { tags: { operation: 'users_create' } });
      toast({
        title: 'Error',
        description: error.message || 'Error inesperado',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
      setSessionLock(false); // Unlock session sync
    }
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;

    setIsSubmitting(true);
    try {
      console.log('[UsersPage] Updating user with formData:', formData, 'user_id:', editingUser.user_id);
      const res = await adminApi.updateUser({
        ...formData,
        user_id: editingUser.user_id
      });

      if (res.success) {
        toast({
          title: 'Usuario actualizado',
          description: 'Los cambios han sido guardados',
        });
        setEditingUser(null);
        resetForm();
        fetchData();
      } else {
        toast({
          title: 'Error',
          description: res.error || 'No se pudo actualizar el usuario',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.error('[UsersPage] Update error:', error);
      Sentry.captureException(error, { tags: { operation: 'users_update' } });
      toast({
        title: 'Error',
        description: error?.message || 'No se pudo actualizar el usuario',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleActive = async (user: User) => {
    try {
      const res = await adminApi.updateUser({
        user_id: user.user_id,
        active: !user.active
      });

      if (res.success) {
        toast({
          title: 'Estado actualizado',
          description: 'El estado del usuario ha sido cambiado',
        });
        fetchData();
      }
    } catch (error) {
      Sentry.captureException(error, { tags: { operation: 'users_toggle_active' } });
      toast({
        title: 'Error',
        description: 'No se pudo cambiar el estado',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteUser = async (userId: string) => {
    try {
      const res = await adminApi.deleteUser(userId);
      if (res.success) {
        toast({
          title: 'Usuario eliminado',
          description: 'El usuario ha sido eliminado del sistema',
        });
        setDeletingUserId(null);
        fetchData();
      }
    } catch (error: any) {
      console.error('[UsersPage] Delete error:', error);
      Sentry.captureException(error, { tags: { operation: 'users_delete' } });
      toast({
        title: 'Error',
        description: error?.message || 'No se pudo eliminar el usuario',
        variant: 'destructive',
      });
    }
  };

  const handleResetPassword = async (userId: string) => {
    setIsSubmitting(true);
    try {
      const res = await adminApi.resetUserPassword(userId);
      if (res.success) {
        toast({
          title: 'Email enviado',
          description: 'Se ha enviado un correo de recuperación al usuario',
        });
        setResettingUserId(null);
      }
    } catch (error: any) {
      Sentry.captureException(error, { tags: { operation: 'users_reset_password' } });
      toast({
        title: 'Error',
        description: error?.message || 'No se pudo enviar el correo de recuperación',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'gym_staff',
      gym_id: currentUser?.gym_id || '',
      active: true,
    });
  };

  const openEditDialog = (user: User) => {
    setEditingUser(user);
    setFormData({
      name: user.name || '',
      email: user.email,
      password: '', // Don't show existing password
      role: user.role,
      gym_id: user.gym_id || '',
      active: user.active,
    });
  };

  const getRoleBadge = (role: UserRole) => {
    const variants: Record<UserRole, 'default' | 'success' | 'warning' | 'info'> = {
      super_admin: 'default',
      gym_admin: 'success',
      gym_supervisor: 'info',
      gym_staff: 'warning',
      staff: 'warning',
    };
    const labels: Record<UserRole, string> = {
      super_admin: 'Super Admin',
      gym_admin: 'Admin Gym',
      gym_supervisor: 'Supervisor',
      gym_staff: 'Staff',
      staff: 'Staff',
    };
    return <Badge variant={variants[role]}>{labels[role]}</Badge>;
  };

  const availableRoles: { value: UserRole; label: string }[] = isSuperAdmin
    ? [
      { value: 'super_admin', label: 'Super Administrador' },
      { value: 'gym_admin', label: 'Administrador de Gimnasio' },
      { value: 'gym_supervisor', label: 'Supervisor' },
      { value: 'gym_staff', label: 'Staff' },
    ]
    : [
      { value: 'gym_supervisor', label: 'Supervisor' },
      { value: 'gym_staff', label: 'Staff' },
    ];

  return (
    <DashboardLayout
      title="Gestión de Usuarios"
      subtitle={isSuperAdmin ? "Administra todos los usuarios del sistema" : "Administra los usuarios de tu gimnasio"}
      actions={
        <Button onClick={() => { resetForm(); setIsCreateOpen(true); }} variant="gradient">
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Usuario
        </Button>
      }
    >
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Crear Nuevo Usuario</DialogTitle>
            <DialogDescription>
              Agrega un nuevo usuario al sistema
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nombre completo</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Juan Pérez"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="juan@email.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Contraseña</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Min 6 caracteres"
              />
            </div>
            <div className="space-y-2">
              <Label>Rol</Label>
              <Select
                value={formData.role}
                onValueChange={(value: UserRole) => setFormData({ ...formData, role: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {availableRoles.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      {role.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {formData.role !== 'super_admin' && isSuperAdmin && (
              <div className="space-y-2">
                <Label>Gimnasio</Label>
                <Select
                  value={formData.gym_id}
                  onValueChange={(value) => setFormData({ ...formData, gym_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar gimnasio" />
                  </SelectTrigger>
                  <SelectContent>
                    {gyms.map((gym) => (
                      <SelectItem key={gym.gym_id} value={gym.gym_id}>
                        {gym.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateOpen(false)} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button variant="gradient" onClick={handleCreateUser} disabled={isSubmitting}>
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Crear Usuario'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Search */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar usuarios..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            Usuarios ({filteredUsers.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Usuario</TableHead>
                  <TableHead>Rol</TableHead>
                  {isSuperAdmin && <TableHead>Gimnasio</TableHead>}
                  <TableHead>Estado</TableHead>
                  <TableHead>Fecha Registro</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow key={user.user_id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full gradient-primary flex items-center justify-center">
                          <span className="text-sm font-semibold text-primary-foreground">
                            {(user.name || user.email).substring(0, 2).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium">{user.name || 'Sin nombre'}</p>
                          <p className="text-sm text-muted-foreground">{user.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{getRoleBadge(user.role)}</TableCell>
                    {isSuperAdmin && (
                      <TableCell>
                        <span className="text-sm">{getGymName(user.gym_id)}</span>
                      </TableCell>
                    )}
                    <TableCell>
                      <Badge variant={user.active ? 'success' : 'muted'}>
                        {user.active ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString('es-ES') : '-'}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {user.user_id !== currentUser?.user_id && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-500 dark:text-red-400 hover:bg-red-500/10 dark:hover:bg-red-400/20"
                            onClick={() => handleToggleActive(user)}
                            title={user.active ? "Desactivar usuario" : "Activar usuario"}
                          >
                            {user.active ? (
                              <X className="h-4 w-4" />
                            ) : (
                              <Check className="h-4 w-4 text-emerald-500 dark:text-emerald-400" />
                            )}
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(user)}
                          className="text-primary hover:bg-primary/10"
                          title="Editar usuario"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-amber-500 dark:text-amber-400 hover:bg-amber-500/10 dark:hover:bg-amber-400/20"
                          onClick={() => setResettingUserId(user.user_id)}
                          title="Restablecer contraseña"
                        >
                          <Key className="h-4 w-4" />
                        </Button>
                        {user.user_id !== currentUser?.user_id && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-600 dark:text-red-400 hover:bg-red-600/10 dark:hover:bg-red-400/20"
                            onClick={() => setDeletingUserId(user.user_id)}
                            title="Eliminar usuario"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={!!editingUser} onOpenChange={(open) => !open && setEditingUser(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Editar Usuario</DialogTitle>
            <DialogDescription>
              Modifica los datos del usuario
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Nombre completo</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Contraseña (dejar en blanco para no cambiar)</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Rol</Label>
              <Select
                value={formData.role}
                onValueChange={(value: UserRole) => setFormData({ ...formData, role: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {availableRoles.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      {role.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {formData.role !== 'super_admin' && isSuperAdmin && (
              <div className="space-y-2">
                <Label>Gimnasio</Label>
                <Select
                  value={formData.gym_id}
                  onValueChange={(value) => setFormData({ ...formData, gym_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {gyms.map((gym) => (
                      <SelectItem key={gym.gym_id} value={gym.gym_id}>
                        {gym.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingUser(null)} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button variant="gradient" onClick={handleUpdateUser} disabled={isSubmitting}>
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Guardar Cambios'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deletingUserId} onOpenChange={(open) => !open && setDeletingUserId(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>¿Eliminar usuario?</DialogTitle>
            <DialogDescription>
              Esta acción no se puede deshacer. El usuario será eliminado permanentemente del sistema.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeletingUserId(null)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => deletingUserId && handleDeleteUser(deletingUserId)}
            >
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Confirmation Dialog */}
      <Dialog open={!!resettingUserId} onOpenChange={(open) => !open && setResettingUserId(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>¿Restablecer contraseña?</DialogTitle>
            <DialogDescription>
              Se enviará un correo electrónico al usuario con instrucciones para restablecer su contraseña.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setResettingUserId(null)} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button
              variant="gradient"
              onClick={() => resettingUserId && handleResetPassword(resettingUserId)}
              disabled={isSubmitting}
            >
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Key className="h-4 w-4 mr-2" />}
              Enviar Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
